import React, { useState, useRef, useEffect } from 'react';
import { 
  Send, Bot, User, Loader2, Sparkles, AlertCircle, Scale, Paperclip, X, 
  FileText, Image as ImageIcon, Trash2, Info
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { chatAPI } from '../api';

// Claves para localStorage
const CHAT_STORAGE_KEY = 'lexduo_chat_history';
const SESSION_STORAGE_KEY = 'lexduo_chat_session';

const Chat = () => {
  // Estados principales
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(CHAT_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem(SESSION_STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  // Tipos de archivo permitidos
  const ALLOWED_TYPES = {
    'application/pdf': { icon: FileText, label: 'PDF' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, label: 'DOCX' },
    'application/msword': { icon: FileText, label: 'DOC' },
    'text/plain': { icon: FileText, label: 'TXT' },
    'text/csv': { icon: FileText, label: 'CSV' },
    'image/png': { icon: ImageIcon, label: 'PNG' },
    'image/jpeg': { icon: ImageIcon, label: 'JPG' },
    'image/gif': { icon: ImageIcon, label: 'GIF' },
    'image/webp': { icon: ImageIcon, label: 'WEBP' }
  };

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  // Guardar mensajes en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(messages));
    } catch (e) {
      console.error('Error guardando historial:', e);
    }
  }, [messages]);

  useEffect(() => {
    if (sessionId) {
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
      } catch (e) {
        console.error('Error guardando session:', e);
      }
    }
  }, [sessionId]);

  // Scroll al último mensaje
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Limpiar historial
  const clearHistory = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem(CHAT_STORAGE_KEY);
    localStorage.removeItem(SESSION_STORAGE_KEY);
    setError(null);
  };

  // Manejo de archivos
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES[file.type]) {
      setError('Tipo de archivo no soportado. Use: PDF, DOCX, TXT, CSV o imágenes.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setError('El archivo excede el tamaño máximo de 10MB.');
      return;
    }

    setAttachedFile(file);
    setError(null);

    // Preview para imágenes
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setAttachedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Enviar mensaje
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if ((!input.trim() && !attachedFile) || isLoading) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date().toISOString(),
      file: attachedFile ? { name: attachedFile.name, type: attachedFile.type } : null
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const history = messages.map(m => ({
        role: m.role,
        content: m.content
      }));

      let response;
      if (attachedFile) {
        response = await chatAPI.sendWithFile(userMessage.content || 'Analiza este documento', history, attachedFile);
        removeFile();
      } else {
        response = await chatAPI.send({
          message: userMessage.content,
          history,
          session_id: sessionId
        });
      }

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.session_id) {
        setSessionId(response.data.session_id);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar el mensaje');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => {
    if (!content) return '';
    return content.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <h3 key={i} className="font-bold text-lg mt-3 mb-2">{line.replace(/\*\*/g, '')}</h3>;
      }
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return <li key={i} className="ml-4">{line.substring(2)}</li>;
      }
      if (line.match(/^\d+\./)) {
        return <li key={i} className="ml-4 list-decimal">{line.substring(line.indexOf('.') + 1)}</li>;
      }
      return <p key={i} className="mb-1">{line}</p>;
    });
  };

  // Sugerencias de preguntas
  const sugerencias = [
    "¿Cuáles son los plazos para interponer un recurso de apelación?",
    "Explica el procedimiento monitorio en España",
    "¿Qué requisitos tiene una demanda de desahucio?",
    "¿Cómo se calcula la indemnización por despido improcedente?"
  ];

  return (
    <div className="p-6 max-w-4xl mx-auto h-[calc(100vh-120px)] flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center shadow-lg">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Asistente Legal</h1>
              <p className="text-gray-500 text-sm">Consultas sobre derecho español</p>
            </div>
          </div>
          
          {messages.length > 0 && (
            <Button variant="outline" size="sm" onClick={clearHistory} className="text-gray-500 hover:text-red-600">
              <Trash2 className="w-4 h-4 mr-2" />Nueva conversación
            </Button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <Alert className="mb-4 bg-blue-50 border-blue-200">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <strong>Consultas generales:</strong> Para analizar un expediente específico, ve a <strong>Expedientes → [Expediente] → Análisis IA</strong>
        </AlertDescription>
      </Alert>

      {/* Área de mensajes */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardContent className="flex-1 overflow-y-auto p-4">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <Sparkles className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">¿En qué puedo ayudarte?</h2>
              <p className="text-gray-500 mb-6 max-w-md">
                Soy tu asistente legal especializado en derecho español. Puedo ayudarte con consultas sobre procedimientos, plazos, legislación y más.
              </p>
              
              {/* Sugerencias */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-lg">
                {sugerencias.map((sug, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className="text-left text-sm h-auto py-3 px-4 justify-start"
                    onClick={() => setInput(sug)}
                  >
                    <Sparkles className="w-4 h-4 mr-2 flex-shrink-0 text-blue-500" />
                    <span className="line-clamp-2">{sug}</span>
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-900 rounded-bl-md'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {message.role === 'user' ? (
                        <User className="w-4 h-4" />
                      ) : (
                        <Bot className="w-4 h-4 text-blue-600" />
                      )}
                      <span className="text-xs opacity-70">
                        {message.role === 'user' ? 'Tú' : 'Asistente Legal'}
                      </span>
                    </div>
                    
                    {message.file && (
                      <div className="flex items-center gap-2 mb-2 text-xs bg-white/10 px-2 py-1 rounded">
                        <Paperclip className="w-3 h-3" />
                        {message.file.name}
                      </div>
                    )}
                    
                    <div className={`text-sm ${message.role === 'assistant' ? 'prose prose-sm max-w-none' : ''}`}>
                      {message.role === 'assistant' ? formatMessage(message.content) : message.content}
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-sm text-gray-500">Analizando...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </CardContent>

        {/* Input area */}
        <div className="border-t p-4">
          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {attachedFile && (
            <div className="mb-3 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-3">
                {filePreview ? (
                  <img src={filePreview} alt="Preview" className="w-12 h-12 rounded object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-blue-100 rounded flex items-center justify-center">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-sm text-gray-900">{attachedFile.name}</p>
                  <p className="text-xs text-gray-500">
                    {(attachedFile.size / 1024).toFixed(1)} KB • {ALLOWED_TYPES[attachedFile.type]?.label || 'Archivo'}
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={removeFile}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="flex-1 relative">
              <Textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Escribe tu consulta legal..."
                className="min-h-[60px] pr-12 resize-none"
                disabled={isLoading}
              />
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg,.gif,.webp"
                className="hidden"
              />
              
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 bottom-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                <Paperclip className="w-4 h-4 text-gray-400" />
              </Button>
            </div>
            
            <Button
              type="submit"
              disabled={isLoading || (!input.trim() && !attachedFile)}
              className="bg-blue-600 hover:bg-blue-700 self-end"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </form>
          
          <p className="text-xs text-gray-500 mt-2 text-center">
            Puedes adjuntar documentos PDF, DOCX o imágenes para su análisis
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Chat;
